import { useEffect, useRef } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schemaData?: Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schemaData,
}: SEOHeadProps) {
  const fullTitle = `${title} - 韩国漫画网`;
  const schemaInserted = useRef<Set<string>>(new Set());

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    if (canonicalUrl) {
      setMeta('og:url', `https://www.koreancomic.cn${canonicalUrl}`, true);
      let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute('href', `https://www.koreancomic.cn${canonicalUrl}`);
    }
    setMeta('og:locale', 'zh_CN', true);
    setMeta('og:site_name', '韩国漫画网', true);
    if (ogImage) setMeta('og:image', ogImage, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    if (schemaData) {
      schemaData.forEach((schema) => {
        const key = JSON.stringify(schema['@type']) + JSON.stringify(schema.name || '');
        if (!schemaInserted.current.has(key)) {
          schemaInserted.current.add(key);
          const script = document.createElement('script');
          script.type = 'application/ld+json';
          script.textContent = JSON.stringify(schema);
          script.setAttribute('data-schema', key);
          document.head.appendChild(script);
        }
      });
    }

    return () => {
      const scripts = document.head.querySelectorAll('script[data-schema]');
      scripts.forEach((s) => s.remove());
    };
  }, [fullTitle, description, keywords, canonicalUrl, ogType, ogImage, schemaData]);

  return null;
}